/**
 * ELO Rating System Utilities
 * 
 * This module provides functions for calculating and updating Elo ratings
 * for both club-specific and global ratings.
 */

/** Current competitive formula: result-score bands + K steps at 5 / 10 / 25. */
export const ELO_POLICY_V2 = 'v2';
/** Pre-v2 formula: full win/loss, balanced scoreline nudge, K steps at 5 / 15 / 30. */
export const ELO_POLICY_LEGACY = 'legacy';
export const ELO_POLICY_CURRENT = ELO_POLICY_V2;
/** Games with created_at on or after this instant use v2. Earlier games keep legacy on score correction. */
export const ELO_POLICY_V2_EFFECTIVE_AT = '2026-10-07T13:00:00.000Z';
export const ELO_ALGORITHM_VERSION = 'elo_v2_team_avg_k_5_10_25_result_score_certainty';
export const ELO_BASE_K_FACTOR = 24;

/**
 * How much a player's rating can be trusted, from their K.
 * K=40 → 0.6, K=32 → 0.75, K=24 or 16 → 1.
 */
export const ratingCertainty = (gamesPlayed, eloPolicy = ELO_POLICY_CURRENT) => {
  const k = calculateKFactor(gamesPlayed, false, eloPolicy);
  if (!k) return 1;
  return Math.min(1, ELO_BASE_K_FACTOR / k);
};

/**
 * Average certainty of the other players in the game (partner + opponents).
 * @param {number[]} othersGamesPlayed
 */
export const matchCertainty = (othersGamesPlayed = [], eloPolicy = ELO_POLICY_CURRENT) => {
  const list = (othersGamesPlayed || []).filter((n) => n != null);
  if (!list.length) return 1;
  const sum = list.reduce((acc, games) => acc + ratingCertainty(games, eloPolicy), 0);
  return sum / list.length;
};

/**
 * @param {string|Date|null|undefined} gameCreatedAt
 * @returns {typeof ELO_POLICY_V2 | typeof ELO_POLICY_LEGACY}
 */
export const resolveEloPolicy = (gameCreatedAt) => {
  if (gameCreatedAt == null || gameCreatedAt === '') return ELO_POLICY_CURRENT;
  const ms = new Date(gameCreatedAt).getTime();
  if (Number.isNaN(ms)) return ELO_POLICY_CURRENT;
  return ms < new Date(ELO_POLICY_V2_EFFECTIVE_AT).getTime()
    ? ELO_POLICY_LEGACY
    : ELO_POLICY_V2;
};

/**
 * Calculate K-factor based on player's games played for a specific game type
 * @param {number} gamesPlayed - Number of games played for this specific game type
 * @param {boolean} isGlobal - Whether this is for global Elo (uses reduced K)
 * @param {string} [eloPolicy=ELO_POLICY_CURRENT]
 * @returns {number} - The appropriate K-factor
 */
export const calculateKFactor = (
  gamesPlayed,
  isGlobal = false,
  eloPolicy = ELO_POLICY_CURRENT
) => {
  const globalMultiplier = isGlobal ? 0.3125 : 1;
  const n = Number(gamesPlayed) || 0;
  const useLegacy = eloPolicy === ELO_POLICY_LEGACY;

  if (n < 5) return 40 * globalMultiplier;
  if (useLegacy) {
    if (n < 15) return 32 * globalMultiplier;
    if (n < 30) return 24 * globalMultiplier;
    return 16 * globalMultiplier;
  }
  if (n < 10) return 32 * globalMultiplier;
  if (n < 25) return 24 * globalMultiplier;
  return 16 * globalMultiplier;
};

/**
 * Calculate expected score based on Elo ratings
 * @param {number} ratingA - Rating of player A
 * @param {number} ratingB - Rating of player B
 * @returns {number} - Expected score for player A (between 0 and 1)
 */
export const calculateExpectedScore = (ratingA, ratingB) => {
  return 1 / (1 + Math.pow(10, (ratingB - ratingA) / 400));
};

/**
 * Calculate Elo change
 * @param {number} rating - Current rating
 * @param {number} opponentRating - Opponent's rating
 * @param {number} actualScore - Actual score (1 for win, 0 for loss, 0.5 for draw)
 * @param {number} kFactor - K-factor to use
 * @param {number} marginMultiplier - Optional multiplier based on score margin
 * @returns {number} - The Elo change
 */
export const calculateEloChange = (rating, opponentRating, actualScore, kFactor, marginMultiplier = 1) => {
  const expectedScore = calculateExpectedScore(rating, opponentRating);
  return Math.round(kFactor * (actualScore - expectedScore) * marginMultiplier);
};

/**
 * Get club strength rating
 * @param {string} clubId - Club ID
 * @param {Object} clubStrengths - Object mapping club IDs to strength ratings
 * @returns {number} - The club strength rating
 */
export const getClubStrength = (clubId, clubStrengths) => {
  return clubStrengths[clubId] || 1500;
}; 

/**
 * Get the appropriate game count for a specific game type
 * @param {Object} player - Player object with game count fields
 * @param {string} gameType - Game type ('mixed', 'levels', 'singles', 'unbalanced')
 * @returns {number} - Number of games played for this game type
 */
export const getGameCountForType = (player, gameType) => {
  switch (gameType) {
    case 'mixed':
      return player.mixed_games_played || 0;
    case 'levels':
      return player.levels_games_played || 0;
    case 'singles':
      return player.singles_games_played || 0;
    case 'unbalanced':
      return player.unbalanced_games_played || 0;
    default:
      // Fallback to total games if game type is unknown
      return (player.mixed_games_played || 0) + 
             (player.levels_games_played || 0) + 
             (player.singles_games_played || 0) + 
             (player.unbalanced_games_played || 0);
  }
};

/**
 * Calculate K-factor for a specific game type
 * @param {Object} player - Player object with game count fields
 * @param {string} gameType - Game type ('mixed', 'levels', 'singles', 'unbalanced')
 * @param {boolean} isGlobal - Whether this is for global Elo
 * @returns {number} - The appropriate K-factor for this game type
 */
export const calculateKFactorForGameType = (player, gameType, isGlobal = false) => {
  const gamesPlayed = getGameCountForType(player, gameType);
  return calculateKFactor(gamesPlayed, isGlobal);
}; 